import { Cascader, type CascaderProps } from 'antd';
import React, { useEffect, useState } from 'react';
import { getRegionTree } from '@/services/billiard';

interface RegionCascaderProps extends Omit<CascaderProps<any>, 'options'> {
  value?: string[];
  onChange?: (value: string[]) => void;
}

interface CascaderOption {
  value: string;
  label: string;
  children?: CascaderOption[];
}

// 全局缓存，避免重复请求
let regionTreeCache: CascaderOption[] | null = null;
let loadingPromise: Promise<CascaderOption[]> | null = null;

/**
 * 省市区级联选择器组件
 * 从服务端加载完整的省市区树形数据
 * 支持全局缓存，避免重复请求
 */
const RegionCascader: React.FC<RegionCascaderProps> = ({ value, onChange, ...restProps }) => {
  const [options, setOptions] = useState<CascaderOption[]>([]);
  const [loading, setLoading] = useState(false);

  // 转换后端数据为级联选择器需要的格式
  const transformRegionData = (regions: BILLIARD.Region[]): CascaderOption[] => {
    return regions.map((region) => ({
      value: region.name, // 使用name作为value，而不是id
      label: region.name,
      children: region.children ? transformRegionData(region.children) : undefined,
    }));
  };

  // 加载省市区树形数据
  const loadRegionTree = async (): Promise<CascaderOption[]> => {
    // 如果有缓存，直接返回
    if (regionTreeCache) {
      return regionTreeCache;
    }

    // 如果正在加载，等待加载完成
    if (loadingPromise) {
      return loadingPromise;
    }

    // 创建加载Promise
    loadingPromise = (async () => {
      try {
        const res = await getRegionTree();
        if (res.code === 200 && res.data) {
          const transformedData = transformRegionData(res.data);
          regionTreeCache = transformedData; // 缓存数据
          return transformedData;
        }
        return [];
      } catch (error) {
        console.error('加载省市区数据失败:', error);
        return [];
      } finally {
        loadingPromise = null; // 清除加载Promise
      }
    })();

    return loadingPromise;
  };

  // 组件挂载时加载数据
  useEffect(() => {
    const init = async () => {
      setLoading(true);
      const data = await loadRegionTree();
      setOptions(data);
      setLoading(false);
    };
    init();
  }, []);

  return (
    <Cascader
      placeholder="请选择省市区"
      loading={loading}
      value={value}
      onChange={onChange as any}
      options={options}
      showSearch
      {...restProps}
    />
  );
};

export default RegionCascader;
